import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import type {
  AuditAccessRequest,
  CreateAuditRequest,
  ApproveAuditRequest,
  DenyAuditRequest,
  AuditRequestStatus,
} from "../types/audit";

/**
 * Fetch auditor's own access requests
 */
export function useAuditRequests(status?: AuditRequestStatus) {
  return useQuery({
    queryKey: ["audit-requests", status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);

      const { data } = await api.get(
        `/audit/access-requests?${params.toString()}`,
      );
      return data.data as AuditAccessRequest[];
    },
  });
}

/**
 * Fetch access requests for a specific facility (managers only)
 */
export function useFacilityAuditRequests(
  facilityId: string,
  status?: AuditRequestStatus,
) {
  return useQuery({
    queryKey: ["facility-audit-requests", facilityId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);

      const { data } = await api.get(
        `/facilities/${facilityId}/audit-requests?${params.toString()}`,
      );
      return data.data as AuditAccessRequest[];
    },
    enabled: !!facilityId,
  });
}

/**
 * Create audit access request
 */
export function useCreateAuditRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateAuditRequest) => {
      const { data } = await api.post("/audit/access-requests", request);
      return data.data as AuditAccessRequest;
    },
    // US-081: AuditRequestForm catches with handleGenericFormError.
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-requests"] });
    },
  });
}

/**
 * Approve audit access request (managers only)
 */
export function useApproveAuditRequest(requestId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (approval: ApproveAuditRequest) => {
      const { data } = await api.put(
        `/audit/access-requests/${requestId}/approve`,
        approval,
      );
      return data.data;
    },
    // US-081: AuditRequestApprovalModal owns error UX via bespoke catch toasts.
    // Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-requests"] });
      queryClient.invalidateQueries({ queryKey: ["facility-audit-requests"] });
      queryClient.invalidateQueries({ queryKey: ["auditor-grants"] });
      queryClient.invalidateQueries({ queryKey: ["audit-group-homes"] });
    },
  });
}

/**
 * Deny audit access request (managers only)
 */
export function useDenyAuditRequest(requestId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (denial: DenyAuditRequest) => {
      const { data } = await api.put(
        `/audit/access-requests/${requestId}/deny`,
        denial,
      );
      return data.data;
    },
    // US-081: AuditRequestApprovalModal owns error UX via bespoke catch toasts.
    // Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-requests"] });
      queryClient.invalidateQueries({ queryKey: ["facility-audit-requests"] });
    },
  });
}
